import * as React from 'react';
import { handleEnterOrSpaceKeyPress } from '../../../Accessibility/KeyboardEvents';
import { completionStatus } from '../../../Progresso';
import { ActiveProgramModule } from '../../../ProgramStore/Types';
import { AlreadyTranslated, T } from '../../../Translations';
import CompleteSegmentIcon from '../../Cutesies/CompleteSegmentIcon';
import { defaultTheme } from '../../../Themes';
import OutlineLockIcon from '../OutlineLockIcon';
import { isModuleLocked } from '../locked';
import * as style from '../style.module.css';

interface Props {
  programModule: ActiveProgramModule;
  open: boolean;
  isViewedModule: boolean;
  toggle: (id: number) => void;
}

// A solid green dot (no check) marks a module you've reached but not finished. It stays
// green rather than grey — a grey dot read as clickable/unreached, which was misleading.
const StartedDotIcon: React.FC = () => (
  <svg viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
    <circle cx="11" cy="11" r="11" fill="#1a8927" />
  </svg>
);

// A hollow grey ring marks a module the learner hasn't started. The open shape (vs the
// filled green in-progress dot) conveys status without relying on colour alone (WCAG 1.4.1);
// same grey as the unstarted segment dots so the two read as one family.
const NotStartedModuleIcon: React.FC = () => (
  <svg viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
    <circle
      cx="11"
      cy="11"
      r="8.4"
      fill="none"
      stroke={defaultTheme.variables['icon-standard-on-dark']}
      strokeWidth="2.6"
    />
  </svg>
);

const OutlineModule: React.FC<Props> = ({ programModule, open, isViewedModule, toggle }) => {
  const expandable = !programModule.isGated;

  // One exhaustive display state drives the module's class, icon, and screen-reader label.
  // `locked` (gated or fully-inaccessible; see isModuleLocked) takes precedence over the
  // completionStatus of the module's percentComplete — canonical ModuleIcon has no lock case,
  // so folding it in here keeps the padlock for gated / fully-inaccessible modules.
  type ModuleDisplay = 'locked' | 'complete' | 'in-progress' | 'unstarted';
  const display: ModuleDisplay = isModuleLocked(programModule)
    ? 'locked'
    : completionStatus(programModule.percentComplete);

  const onToggle = () => {
    if (expandable) {
      toggle(programModule.id);
    }
  };

  const moduleClass = () => {
    switch (display) {
      case 'locked':
        return style.inaccessible;
      case 'complete':
        return style.complete;
      case 'in-progress':
        return style.current;
      case 'unstarted':
        return style.unstarted;
    }
  };

  const moduleIcon = () => {
    switch (display) {
      case 'locked':
        return <OutlineLockIcon />;
      case 'complete':
        return <CompleteSegmentIcon />;
      case 'in-progress':
        return <StartedDotIcon />;
      case 'unstarted':
        return <NotStartedModuleIcon />;
    }
  };

  const moduleStatusLabel = () => {
    switch (display) {
      case 'locked':
        return <T kind="Locked" />;
      case 'complete':
        return <T kind="Completed" />;
      case 'in-progress':
        return <T kind="In Progress" />;
      case 'unstarted':
        return <T kind="Not Started" />;
    }
  };

  return (
    <li className={style.module} data-test-outline-module={programModule.id}>
      <div
        className={`${moduleClass()} ${expandable ? style.moduleToggle : ''} ${
          isViewedModule ? style.moduleViewed : ''
        }`}
        role={expandable ? 'button' : undefined}
        aria-expanded={expandable ? open : undefined}
        tabIndex={expandable ? 0 : undefined}
        onClick={onToggle}
        onKeyPress={expandable ? handleEnterOrSpaceKeyPress(onToggle) : undefined}
        data-intercom-target="outline-module-toggle"
        data-test-module-toggle={programModule.id}
      >
        <div className={style.moduleIcon} data-test-module-icon={display}>
          {moduleIcon()}
          <div className={style.moduleLine} />
        </div>
        <div className={style.moduleLabel} data-test-module-title={programModule.title}>
          <AlreadyTranslated content={programModule.title} />
          <span className={style.srOnly}>{moduleStatusLabel()}</span>
        </div>
        {expandable && (
          <div
            className={`${style.moduleCaret} ${open ? style.moduleCaretOpen : ''}`}
            aria-hidden={true}
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
    </li>
  );
};
export default OutlineModule;
